import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import FilePlayer from 'react-player/file';
import { GatsbyImage } from 'gatsby-plugin-image';
import VimeoPlayer from 'react-player/vimeo';
import { generateClassName, stripTags } from '@klv/shared/utils/functions';
import { useEagerLoadingContext } from '@klv/context/eager-loading';
import { useTranslation } from '@klv/hooks/useTranslation';

import * as s from './styles/decorativeVideo.module.css';

import type { TDecorativeVideo } from './types';

const DecorativeVideo = (
  { caption = '', className, loop = false, poster, posterImageData, title = '', url, ...props }: TDecorativeVideo,
) => {
  const { t } = useTranslation();

  const eagerLoad = useEagerLoadingContext();
  
  const [hasEverEnteredView, setHasEverEnteredView] = useState(false);
  const { ref, inView } = useInView({ threshold: 0.25 });

  if (inView && !hasEverEnteredView) {
    setHasEverEnteredView(true);
  }

  const captionText = caption && stripTags(caption);
  let ariaLabel = title;

  if (ariaLabel && captionText) {
    ariaLabel += `: ${captionText}`;
  } else if (captionText) {
    ariaLabel = captionText;
  }

  ariaLabel = ariaLabel && `(Video) ${ariaLabel.trim()}`;

  if (url) {
    const vimeoPlayerConfig = {
      playerOptions: {
        autopause: false,
        background: true,
        keyboard: false,
        loop,
        responsive: true,
        texttrack: t('langCodes_recaptchaLanguage') === 'en' ? '' : t('langCodes_recaptchaLanguage'),
      },
    };

    return (
      <div className={generateClassName([s.videoWrapper, className])} ref={ref}>
        <GatsbyImage alt="" image={posterImageData} loading={eagerLoad} />
        {hasEverEnteredView && (
            <VimeoPlayer
              className={s.vimeoPlayer}
              aria-label={ariaLabel}
              config={vimeoPlayerConfig}
              playing={inView}
              url={url}
              height="100%"
              width="100%"
              muted
              volume={0}
              playsinline
              {...props}
            />
        )}
      </div>
    );
  }

  return (
    <div className={generateClassName([s.videoWrapper, className])} ref={ref}>
      {hasEverEnteredView && (
          <FilePlayer
            aria-label={ariaLabel}
            config={poster ? { attributes: { poster } } : {}}
            loop={loop}
            muted
            playing={inView}
            playsinline
            height="100%"
            width="100%"
            volume={0}
            {...props}
          />
      )}
    </div>
  );
};

export default DecorativeVideo;
